import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../../Config';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${config.apiBaseUrl}/admin/upsale/product/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json();

        if (data.success && data.product) {
          setProduct(data.product);
        } else {
          throw new Error();
        }
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const getParsedFeatures = () => {
    if (!product?.keyFeatures) return [];

    try {
      let parsed = product.keyFeatures;

      if (typeof parsed[0] === 'string') {
        parsed = JSON.parse(parsed[0]);
        if (typeof parsed === 'string') {
          parsed = JSON.parse(parsed);
        }
      }

      return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
    } catch {
      return [];
    }
  };

  const features = getParsedFeatures();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box textAlign="center" mt={5}>
        <Typography color="error">Failed to load product.</Typography>
      </Box>
    );
  }

  return (
    <Paper
      sx={{
        p: isSmDown ? 2 : 4,
        maxWidth: 900,
        mx: 'auto',
        mt: 4,
        borderRadius: 3,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          Product Details
        </Typography>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Box display="flex" flexDirection={isSmDown ? 'column' : 'row'} gap={3}>
        <Box flex={1}>
          {!imageError ? (
            <img
              src={product.image}
              alt={product.name}
              onError={() => setImageError(true)}
              style={{
                width: '100%',
                height: isSmDown ? 200 : 300,
                objectFit: 'cover',
                borderRadius: 10,
              }}
            />
          ) : (
            <Box
              height={isSmDown ? 200 : 300}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="#f5f5f5"
              borderRadius={2}
            >
              <Typography variant="body2">Image not available</Typography>
            </Box>
          )}
        </Box>

        <Box flex={2}>
          <Typography variant="h5" gutterBottom>
            {product.name}
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Description:</strong> {product.description}
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Status:</strong>{' '}
            <Chip
              label={product.isActive ? 'Active' : 'Inactive'}
              color={product.isActive ? 'success' : 'warning'}
              size="small"
            />
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Key Features:</strong>
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {features.length > 0 ? (
              features.map((feature, idx) => <Chip key={idx} label={feature} variant="outlined" />)
            ) : (
              <Typography variant="body2">No features available</Typography>
            )}
          </Stack>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" color="text.secondary">
        <strong>Created At:</strong> {new Date(product.createdAt).toLocaleString()}
      </Typography>

      <Typography variant="body2" color="text.secondary">
        <strong>Updated At:</strong> {new Date(product.updatedAt).toLocaleString()}
      </Typography>
    </Paper>
  );
};

export default ProductDetail;
